import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Keyboard, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from 'utils/classnames';
import { DISPOSITION_COLORS, PALLET_LICENSE_STATUS_TYPES, SCREEN_TYPES } from 'utils/constants';
import { useAppStore } from 'store';
import { Button } from 'components/ui/Button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { useKeyboardInputToggle } from 'hooks/useKeyboardInputToggle';
import { usePalletBuildStore } from 'store/palletBuildStore';
import { playCallbackSound, playErrorSound, playGoodSound } from 'utils/sounds';
import { isValidLicenseId } from 'utils/validation';
import { translateProblemMessage } from 'utils/problemMessages';
import { useTranslation } from 'react-i18next';

export const PalletScanPage = () => {
  const { t } = useTranslation();
  const [scanValue, setScanValue] = useState('');
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { keyboardVisible, toggleKeyboardInput } = useKeyboardInputToggle(inputRef);
  const { setCurrentScreen } = useAppStore();
  const { pallet_id, loadPallet, pallet } = usePalletBuildStore();
  const assignedCartons = pallet?.palletDetail ?? [];

  // If we already have an active pallet, its details are already in the store, skip straight
  // to assigning cases instead of making the user re-scan it.
  useEffect(() => {
    if (pallet_id) setCurrentScreen(SCREEN_TYPES.PALLET_BUILD_ASSIGN_CASES);
  }, [pallet_id, setCurrentScreen]);

  const handleSubmit = async () => {
    const trimmed = scanValue.trim();
    if (!trimmed) return;
    setHasError(false);
    if (!isValidLicenseId(trimmed)) {
      setErrorMessage(t('palletScan.palletIdInvalid'));
      setHasError(true);
      playErrorSound();
      return;
    }
    toast(t('palletScan.scanning'));
    try {
      const result = await loadPallet(trimmed);
      if (result.responseState !== 'confirmed') {
        setErrorMessage(translateProblemMessage(result.responseMsg) || t('palletScan.scanFailed'));
        setHasError(true);
        playErrorSound();
        return;
      }

      setScanValue('');
      result.license_status === PALLET_LICENSE_STATUS_TYPES.CLOSED
        ? playCallbackSound()
        : playGoodSound();
      toast.success(
        t(
          result.license_status === PALLET_LICENSE_STATUS_TYPES.NEW
            ? 'palletScan.newPallet'
            : 'palletScan.scanned'
        )
      );
      setCurrentScreen(SCREEN_TYPES.PALLET_BUILD_ASSIGN_CASES);
    } catch {
      setErrorMessage(t('palletScan.scanFailed'));
      setHasError(true);
      playErrorSound();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const onCancel = () => {
    setScanValue('');
    setCurrentScreen(SCREEN_TYPES.PALLET_BUILD);
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-4 py-4',
        hasError && `-mx-4 -mb-6 min-h-full px-6 pb-6 pt-4 ${DISPOSITION_COLORS.PROBLEM.gradient}`
      )}
    >
      <div className="flex flex-col items-center gap-1">
        <span
          className={cn(
            'text-xs font-semibold uppercase tracking-widest',
            hasError ? 'text-white/80' : 'text-emerald-500'
          )}
        >
          {t('palletScan.eyebrow')}
        </span>
        <h2 className={cn('text-2xl font-bold', hasError ? 'text-white' : 'text-slate-800')}>
          {t('palletScan.title')}
        </h2>
        {hasError && <p className="text-3xl font-bold text-white text-center">{errorMessage}</p>}
      </div>

      <div className="flex items-center gap-3 rounded-xl bg-emerald-50 px-4 py-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100">
          <Layers className="h-4 w-4 text-emerald-600" />
        </div>
        <div className="flex flex-col">
          <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">
            {t('palletScan.createOrSelect')}
          </span>
          <span className="text-sm text-slate-400">{t('palletScan.scanToBegin')}</span>
        </div>
      </div>

      {assignedCartons.length > 0 && (
        <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 px-4 py-2.5">
          <p className="text-xs text-emerald-600">
            {t('palletScan.currentPalletInfo', { count: assignedCartons.length })}
          </p>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <Label className="mb-2 block text-sm font-semibold text-slate-600">
          {t('palletScan.palletBarcode')}
        </Label>
        <div className="flex items-center gap-2">
          <Input
            ref={inputRef}
            type="text"
            inputMode={keyboardVisible ? 'text' : 'none'}
            placeholder={t('palletScan.placeholder')}
            value={scanValue}
            onChange={(e) => setScanValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => requestAnimationFrame(() => inputRef.current?.focus())}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            className="text-base"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={toggleKeyboardInput}
            aria-pressed={keyboardVisible}
            className="h-12 w-12 shrink-0 rounded-xl border-slate-300"
          >
            <Keyboard className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="buttons-container !bg-transparent">
        <Button onClick={onCancel} variant="secondary" className="flex-1 h-12">
          {t('palletScan.back')}
        </Button>
      </div>
    </div>
  );
};
